import {
  UseQueryOptions,
  UseSuspenseQueryOptions,
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { getQueryKey, rethrowDecodedBlobError, saveBlobResponse } from '@feature/utils';

import * as api from './UserDocumentApi';
import {
  UserDocumentAdminDelete,
  UserDocumentAdminDownload,
  UserDocumentAdminPage,
  UserDocumentAdminParams,
  UserDocumentAdminUploadRequest,
  UserDocumentDownload,
  UserDocumentExportParams,
  UserDocumentKind,
  UserDocumentScope,
  UserDocumentSlot,
  UserDocumentSummary,
  UserDocumentUploadRequest,
  UserDocumentsRow,
} from './UserDocumentTypes';

export const userDocumentKey = getQueryKey('userDocument');

export function getUserDocumentSlotsOptions(): UseQueryOptions<UserDocumentSlot[]> {
  return {
    queryKey: userDocumentKey.list(),
    queryFn: () => api.getMine(),
  };
}

export function useUserDocumentUpload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UserDocumentUploadRequest) => api.upload(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: userDocumentKey.root(),
      }),
  });
}

export function useUserDocumentDownload() {
  return useMutation({
    mutationFn: (request: UserDocumentDownload) =>
      api
        .download(request.kind)
        .then(response => saveBlobResponse(response, request.fallbackFilename))
        .catch(rethrowDecodedBlobError),
  });
}

export function useUserDocumentDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (kind: UserDocumentKind) => api.deleteByKind(kind),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: userDocumentKey.root(),
      }),
  });
}

export function getUserDocumentUsersPagedOptions(
  params: UserDocumentAdminParams,
): UseSuspenseQueryOptions<UserDocumentAdminPage> {
  return {
    queryKey: userDocumentKey.paged(params),
    queryFn: () => api.getUsersPaged(params),
  };
}

// Non-suspense on purpose: a failed summary must not blank the listing beneath it.
export function getUserDocumentSummaryOptions(params: UserDocumentScope): UseQueryOptions<UserDocumentSummary> {
  return {
    queryKey: userDocumentKey.as(['summary', params]),
    queryFn: () => api.getSummary(params),
    placeholderData: keepPreviousData,
  };
}

// Non-suspense on purpose: a failed panel must not blank the listing beneath it.
export function getUserDocumentUserOptions(userId: number): UseQueryOptions<UserDocumentsRow> {
  return {
    queryKey: userDocumentKey.one(userId),
    queryFn: () => api.getUser(userId),
    placeholderData: keepPreviousData,
  };
}

export function useUserDocumentAdminUpload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UserDocumentAdminUploadRequest) => api.uploadFor(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: userDocumentKey.root(),
      }),
  });
}

export function useUserDocumentAdminDownload() {
  return useMutation({
    mutationFn: (request: UserDocumentAdminDownload) =>
      api
        .downloadFor(request.userId, request.kind)
        .then(response => saveBlobResponse(response, request.fallbackFilename))
        .catch(rethrowDecodedBlobError),
  });
}

export function useUserDocumentAdminDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UserDocumentAdminDelete) => api.deleteFor(request.userId, request.kind),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: userDocumentKey.root(),
      }),
  });
}

export function useUserDocumentArchiveDownload() {
  return useMutation({
    mutationFn: (params: UserDocumentScope) =>
      api
        .downloadArchive(params)
        .then(response => saveBlobResponse(response, 'Documente.zip'))
        .catch(rethrowDecodedBlobError),
  });
}

export function useUserDocumentExportDownload() {
  return useMutation({
    mutationFn: (params: UserDocumentExportParams) =>
      api
        .downloadExport(params)
        .then(response => saveBlobResponse(response, `Documente-${params.lang}.xlsx`))
        .catch(rethrowDecodedBlobError),
  });
}
